import React, { useState, useRef, useEffect } from 'react';
import { Modal, Form, Row, Col, Input, Button, Tooltip, Select, SelectProps } from 'antd';
import { t } from 'i18next';
import HTTPService from '../../../../utils/makerequest';
import { IInventory } from '../../../../interfaces/IItemData';
import { useGridApiContext } from '@mui/x-data-grid';
import { Toast } from 'primereact/toast';
import { StateLoader } from '../../../../redux/StateLoader';
import { IAuthentication } from '../../../../interfaces/authentication';
import 'primereact/resources/themes/lara-light-indigo/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import Dropdown from '../../../../AltisComponent/Dropdown';
import { IUser } from '../../../../interfaces/IUser';

interface FaultRecordModalProps {
  isModalVisibleFault: boolean;
  handleCancelFault(): void;
  inventoryFilter(paginationInfo: { page: number; pageSize: number }): void;
  inventoryData: IInventory[];
  selectedInventoryIds: number[];
  userId: number;
}

const filterOption: SelectProps<any>['filterOption'] = (input, option) => {
  return (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase());
};

const FaultRecordModal: React.FC<FaultRecordModalProps> = ({
  isModalVisibleFault,
  handleCancelFault,
  inventoryFilter,
  inventoryData,
  selectedInventoryIds,
  userId
}) => {
  const [activityData, setActivityData] = useState<any[]>([]);
  const [userData, setUserData] = useState<IUser[]>();
  const [priority, setPriority] = useState<number>();
  const [form] = Form.useForm();
  const loader = new StateLoader();
  const userInfo: IAuthentication = loader.loadAuthState();
  const toast = useRef<Toast>(null);

  useEffect(() => {
    getUserData(userInfo.companyId);
  }, []);

  const fetchActivity = async (companyId: number) => {
    try {
      const result = await HTTPService.GETActivityNames(companyId);
      const filteredActivities = result.filter(activity => activity.maintenanceType === 2);
      setActivityData(filteredActivities);
    } catch (error) {
      console.error(error);
      toast.current?.show({
        severity: 'error',
        summary: t('Error'),
        detail: t('error_message'),
        life: 3000
      });
    }
  };

  const getUserData = (companyId: number) => {
    HTTPService.GET(`/User/allUserName?companyId=${companyId}`)
      .then((res) => {
        if (!res.data) {
          return;
        }
        if (res.status === 200) {
          setUserData(res.data);
        } else {
          toast.current?.show({
            severity: 'error',
            summary: t('errors'),
            detail: res.error.message,
            life: 3000
          });
        }
      })
      .catch((error) => {
        console.error("Error fetching user data:", error);
      });
  };

  const handlePriorityChange = (value: number) => {
    setPriority(value);
  };

  const formItemStyle = {
    marginBottom: '16px',
    width: '100%'
  };

  const inputStyle = {
    width: '100%',
    borderRadius: '5px'
  };

  const FaultRegister = async (values: any, userId: number, selectedInventoryIds: number[], handleCancelFault: () => void) => {
    const { activityId, description, maintenanceResponsible, estimatedCompletion, priority } = values;

    const body = {
      activityId: Number(activityId),
      description,
      estimatedCompletion,
      maintenanceResponsible,
      maintenanceExternalResponsibleName: values.maintenanceExternalResponsibleName,
      maintenanceExternalResponsibleMail: values.maintenanceExternalResponsibleMail,
      maintenanceResponsibleUserId: values.maintenanceResponsibleUserId,
      inventoryId: selectedInventoryIds[0],
      maintenanceCreatedUserId: userId,
      priority: Number(priority)
    };

    try {
      const response = await HTTPService.POST(`/Maintenance/registerFaultRecord`, body);

      if (response.status === 200) {
        toast.current?.show({
          severity: 'success',
          summary: t('success'),
          detail: t('fault_record_success'),
          life: 3000
        });
        handleCancelFault();
      } else {
        toast.current?.show({
          severity: 'error',
          summary: t('errors'),
          detail: t('fault_record_error'),
          life: 3000
        });
      }
    } catch (error) {
      console.error("Fault record registration failed:", error);
      toast.current?.show({
        severity: 'error',
        summary: t('Error'),
        detail: t('fault_record_error'),
        life: 3000
      });
    }
  };

  return (
    <>
      <Toast ref={toast} />
      <Modal
        title={t('fault_record')}
        open={isModalVisibleFault}
        onCancel={handleCancelFault}
        width={800}
        footer={null}
      >
        <div style={{ padding: '20px' }}>
          <Form
            form={form}
            onFinish={(values) => FaultRegister(values, userId, selectedInventoryIds, handleCancelFault)}
            layout="vertical"
          >
            <Row gutter={16}>
              <Col xs={24} sm={24} md={24} lg={24}>
                <Form.Item
                  name='activityId'
                  label={t('activity_name')}
                  rules={[{ required: true, message: t('activity_name') }]}
                  style={formItemStyle}
                >
                  <Select
                    placeholder={t('activity_name')}
                    onFocus={() => fetchActivity(userInfo?.companyId)}
                    style={inputStyle}
                    dropdownStyle={{ minWidth: 300 }}
                    filterOption={filterOption}
                  >
                    {activityData?.map(row => (
                      <Select.Option key={row.activityId} value={row.activityId}>
                        {row.activityName}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={24} md={24} lg={24}>
                <Form.Item
                  label={t('fault_record_desc')}
                  name="description"
                  rules={[{ required: true, message: t('fault_record_desc') }]}
                  style={formItemStyle}
                >
                  <Input
                    placeholder={t('fault_record_desc')}
                    style={inputStyle}
                  />
                </Form.Item>
              </Col>

              {/* <Col xs={24} sm={24} md={24} lg={24}>
                <Form.Item
                  label={t('faultResponsible')}
                  name="maintenanceResponsible"
                  rules={[{ required: true, message: t('faultResponsible') }]}
                  style={formItemStyle}
                >
                  <Input
                    placeholder={t('faultResponsible')}
                    style={inputStyle}
                  />
                </Form.Item>
              </Col> */}

              <Col xs={24} sm={24} md={24} lg={24}>
                <Form.Item
                  label={t('faultResponsible')}
                  name="maintenanceExternalResponsibleName"
                  rules={[{ required: true, message: t('faultResponsible') }]}
                >
                  <Dropdown
                    placeholder={t('faultResponsible')}
                    dataSource={userData}
                    allowCustomValue={true}
                    valueKey="displayName"
                    style={{ width: '100%' }}
                    includeEmptyOption={false}
                    labelKey="displayName"
                    onChange={(value) => {
                      const selectedUser = userData?.find(u => u.displayName === value || u.userId === value);
                      form.setFieldsValue({
                        maintenanceExternalResponsibleName: selectedUser ? selectedUser.displayName : value,
                        maintenanceResponsibleUserId: selectedUser ? selectedUser.userId : null,
                        maintenanceExternalResponsibleMail: selectedUser?.userEmail || undefined
                      });
                    }}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={24} md={24} lg={24}>
                <Form.Item name="maintenanceResponsibleUserId" hidden>
                  <Input type="hidden" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={24} md={24} lg={24}>
                <Form.Item
                  label={t('email')}
                  name="maintenanceExternalResponsibleMail"
                  rules={[{ required: true }]}
                >
                  <Dropdown
                    placeholder={t('email')}
                    dataSource={userData}
                    valueKey="userEmail"
                    labelKey="userEmail"
                    style={{ width: '100%' }}
                    includeEmptyOption={false}
                    allowCustomValue={true}
                  />
                </Form.Item>
              </Col>

              {/* <Col xs={24} sm={24} md={24} lg={24}>
                <Form.Item
                  label={t('estimatedCompletion')}
                  name="estimatedCompletion"
                  rules={[{ required: true, message: t('estimatedCompletion'), pattern: /^[0-9]*$/ }]}
                  style={formItemStyle}
                >
                  <Input min={0} placeholder={t('estimatedCompletion')} style={inputStyle} />
                </Form.Item>
              </Col> */}


              <Col xs={24} sm={24} md={24} lg={24}>
                <Form.Item
                  name="priority"
                  label={t('Priority')}
                  rules={[{ required: true, message: t('Priority') }]}
                  style={formItemStyle}
                >
                  <Select
                    placeholder={t('Priority')}
                    style={inputStyle}
                    onChange={handlePriorityChange}
                  >
                    <Select.Option value={0}>{t('Low')}</Select.Option>
                    <Select.Option value={1}>{t('Medium')}</Select.Option>
                    <Select.Option value={2}>{t('High')}</Select.Option>
                    <Select.Option value={3}>{t('Highest')}</Select.Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  shape="round"
                  size="middle"
                  style={{ width: 'auto', borderRadius: '10px' }}
                >
                  {t('fault_record')}
                </Button>
              </Col>
            </Row>
          </Form>
        </div>
      </Modal>
    </>
  );
};

export default FaultRecordModal;
